import cloneDeep from 'lodash.clonedeep';

const DEV_PROXY_CONTEXT_HEADER = 'x-dev-proxy-context';

function getSsrProxyHeaders(baseURL) {
  if (process.env.NODE_ENV === 'production' || !process.server || !baseURL?.startsWith('/')) {
    return {};
  }

  const requestHeaders = useRequestHeaders(['host', 'x-forwarded-host', 'x-forwarded-proto']);
  const forwardedHost = requestHeaders['x-forwarded-host'] || requestHeaders.host;

  return {
    ...(forwardedHost ? {'x-forwarded-host': forwardedHost} : {}),
    ...(requestHeaders['x-forwarded-proto'] ? {'x-forwarded-proto': requestHeaders['x-forwarded-proto']} : {}),
    [DEV_PROXY_CONTEXT_HEADER]: 'ssr'
  };
}

function getFetchHeaders(baseURL, optionsHeaders, bypassAuth, authToken) {
  const resolvedAuthToken = !bypassAuth && (authToken ?? useAuthCookie().currentUserInfo()?.auth_token);

  return {
    ...getSsrProxyHeaders(baseURL),
    ...optionsHeaders,
    ...(resolvedAuthToken ? {'Authorization': `Bearer ${resolvedAuthToken}`} : {})
  };
}

function isUnauthorized(error) {
  return error?.response?.status === 401 || error?.status === 401;
}

const createApi = (baseURL, {defaultBypassAuth = false} = {}) => ({
  buildPathAndQs({path, params}) {
    let url = path;

    if (params?.route) {
      url = buildRouteParams(url, params.route);
    }

    if (params?.query) {
      url = `${url}${buildQueryString(params.query)}`;
    }

    return url;
  },
  getFullUrl({path, params}){
    return `${baseURL}${this.buildPathAndQs({path, params})}`;
  },

  async fetch(path, options = {}, bypassAuth = defaultBypassAuth) {
    const useOptions = cloneDeep(options);
    const {params} = useOptions;
    const url = this.buildPathAndQs({path, params});

    delete useOptions.params;

    const optionsHeaders = useOptions.headers;
    delete useOptions.headers;

    const headers = getFetchHeaders(baseURL, optionsHeaders, bypassAuth);

    try {
      return await $fetch(url, {
        baseURL,
        headers,
        ...useOptions
      });
    } catch (error) {
      if (!process.client || bypassAuth || !isUnauthorized(error)) throw error;

      try {
        await useTokenRefresh().refreshOnce();
      } catch (refreshError) {
        if (isUnauthorized(refreshError)) {
          await useSessionExpiredModal();
          useAuthCookie().clearUserInfoCookie();
          // Go straight to sign-out + Cognito rather than our own /sign_in route: that
          // route still runs with the stale auth cookies present, which can trigger
          // another refresh attempt (and another failed-modal loop) before sign-out
          // ever clears them.
          const {signOutUrl, buildCognitoSignInUrl} = useAuthHost();
          const signInUrl = buildCognitoSignInUrl();
          return navigateTo(`${signOutUrl}?redirect_url=${encodeURIComponent(signInUrl)}`, {external: true});
        }
        throw refreshError;
      }

      const freshToken = useAuthCookie().currentUserInfo()?.auth_token;
      const retryHeaders = getFetchHeaders(baseURL, optionsHeaders, bypassAuth, freshToken);

      return $fetch(url, {baseURL, headers: retryHeaders, ...useOptions});
    }
  },

  post(path, options) {
    return this.fetch(path, {
      method: 'POST',
      ...options
    });
  },
  put(path, options) {
    return this.fetch(path, {
      method: 'PUT',
      ...options
    });
  },
  delete(path, options) {
    return this.fetch(path, {
      method: 'DELETE',
      ...options
    });
  }
});

export const useLocalApi = () => {
  return createApi('/app/api');
}

export const useAuthApi = () => {
  const runtimeConfig = useRuntimeConfig();

  return createApi(
    runtimeConfig.public.apiAuthHost
      ? `${runtimeConfig.public.apiAuthHost}/api/v1`
      : '/auth/api/v1'
  );
};

export const useMarsApiV3 = () => {
  const runtimeConfig = useRuntimeConfig();

  return createApi(
    runtimeConfig.public.apiCoreHost
      ? `${runtimeConfig.public.apiCoreHost}/api/v3`
      : '/core/api/v3'
  );
};

export const useMarsApiV4 = () => {
  const runtimeConfig = useRuntimeConfig();

  return createApi(
    runtimeConfig.public.apiCoreHost
      ? `${runtimeConfig.public.apiCoreHost}/api/v4`
      : '/core/api/v4'
  );
};

export const useStoreApi = () => {
  const suffix = 'api/v1';
  const runtimeConfig = useRuntimeConfig();
  let url = ''

  if (process.server && runtimeConfig.public.apiStoreHostServer) {
    url = `${runtimeConfig.public.apiStoreHostServer}/${suffix}`;
  } else {
    url = runtimeConfig.public.apiStoreHost
      ? `${runtimeConfig.public.apiStoreHost}/${suffix}`
      : `/store/${suffix}`;
  }

  return createApi(url);
};

export const useJupiterApi = () => {
  const runtimeConfig = useRuntimeConfig();

  return createApi(
    runtimeConfig.public.apiJupiterHost
      ? `${runtimeConfig.public.apiJupiterHost}/api/v1`
      : '/jupiter/api/v1'
  );
};

export const useDesignApi = () => {
  const runtimeConfig = useRuntimeConfig();

  return createApi(
    runtimeConfig.public.apiDesignHost
      ? `${runtimeConfig.public.apiDesignHost}/api/v1`
      : '/core/design_api/v1'
  );
};

export const useNotificationsApi = () => {
  const runtimeConfig = useRuntimeConfig();

  return createApi(
    runtimeConfig.public.apiNotificationsHost
      ? `${runtimeConfig.public.apiNotificationsHost}/api/v1`
      : '/notification5/api/v1'
  );
};

export const useConversionApi = () => {
  const runtimeConfig = useRuntimeConfig();

  return createApi(
    runtimeConfig.public.apiConversionHost
      ? `${runtimeConfig.public.apiConversionHost}/customer_api/v1`
      : '/conversion/customer_api/v1'
  );
};

export const useCmsApi = () => {
  const runtimeConfig = useRuntimeConfig();

  return createApi(`${runtimeConfig.public.apiCmsHost}/api`, {defaultBypassAuth: true});
}

/*export const useDownloadsApi = () => {
  const runtimeConfig = useRuntimeConfig();

  return createApi(
    runtimeConfig.public.downloadsHost
      ? `${runtimeConfig.public.downloadsHost}/api/v3`
      : '/notifications/api/v3'
  );
};*/
